/**
 * Compress a single image File on the client so it fits comfortably
 * under Graph API's 4 MB simple-upload cap. Uses canvas re-encode to
 * JPEG which is a good size/quality trade-off for site photography.
 *
 * The output File keeps the original name (extension coerced to .jpg)
 * so the SharePoint filename remains human-legible.
 *
 * Returns the original File unchanged if it's already <= maxBytes AND
 * a JPEG/PNG smaller than the target longest edge. Otherwise re-encodes.
 *
 * @param {File} file
 * @param {object} [opts]
 * @param {number} [opts.maxLongestEdge=1920]  target longest dimension
 * @param {number} [opts.quality=0.85]         JPEG encoder quality (0-1)
 * @param {number} [opts.maxBytes=3.5e6]       size to stay under
 */
export async function compressImage(file, opts = {}) {
  const maxLongestEdge = opts.maxLongestEdge || 1920;
  const quality = opts.quality ?? 0.85;
  const maxBytes = opts.maxBytes ?? 3.5 * 1024 * 1024;
  if (!file || !file.type?.startsWith('image/')) return file;

  // Fast path — already small enough, skip re-encode.
  if (file.size <= maxBytes) {
    // Still resize very-large-pixel images (some phones return low-KB
    // HEICs converted to huge PNGs on paste).
    const dims = await readImageDimensions(file);
    if (Math.max(dims.width, dims.height) <= maxLongestEdge) return file;
  }

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxLongestEdge / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(w, h)
    : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, w, h);

  const blob = canvas.convertToBlob
    ? await canvas.convertToBlob({ type: 'image/jpeg', quality })
    : await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.width, height: img.height }); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}
